import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, ChevronDown, ChevronUp, Lock, Car } from "lucide-react";
import { api } from "../../lib/api";
import { BrutalCard, Badge, SectionTitle, BrutalButton } from "../../components/common/Brutal";

export default function CamperQRCodes() {
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/bookings/me").then(({ data }) => {
      // Include retrieved bookings
      const retrieved = JSON.parse(localStorage.getItem("kf_retrieved_bookings") || "[]");
      const ids = new Set(data.map((x) => x.id));
      const merged = [...data, ...retrieved.filter((r) => !ids.has(r.id))];
      setBookings(merged);
      const m = {};
      merged.forEach((b) => (m[b.id] = true));
      setOpen(m);
    });
  }, []);

  const downloadQR = (camperId, name) => {
    const canvas = document.getElementById(`qr-${camperId}`);
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `kaogma-${name.replace(/\s/g, "-")}-${camperId.slice(0, 6)}.png`;
    a.click();
  };

  const confirmed = bookings.filter((b) => b.payment_status === "confirmed" && !b.qr_invalid);
  const pending = bookings.filter((b) => b.payment_status === "unconfirmed");

  return (
    <div className="space-y-6">
      <SectionTitle kicker="Your QR Pass" title="Show this at the gate" />

      {pending.length > 0 && (
        <BrutalCard className="p-5 bg-[#FFB400]" data-testid="qr-pending-notice">
          <div className="flex items-center gap-3">
            <Lock className="h-6 w-6 text-[#3D1E6D]" strokeWidth={2.75} />
            <div>
              <div className="display text-xl text-[#3D1E6D]">QR Codes Pending</div>
              <p className="text-sm text-[#3D1E6D]/80 mt-1">
                You have {pending.length} booking{pending.length > 1 ? "s" : ""} awaiting admin payment confirmation. QR passes will appear here once confirmed.
              </p>
            </div>
          </div>
        </BrutalCard>
      )}

      {confirmed.length === 0 && pending.length === 0 && (
        <BrutalCard className="p-8 text-center" data-testid="no-qr">
          <p className="text-[#6B4E9E]">No QR passes yet. Complete payment and wait for admin confirmation to unlock your passes.</p>
        </BrutalCard>
      )}

      {confirmed.map((b) => (
        <BrutalCard key={b.id} className="p-5" data-testid={`qr-booking-${b.id}`}>
          <button type="button" onClick={() => setOpen({ ...open, [b.id]: !open[b.id] })} className="w-full flex items-center justify-between">
            <div className="text-left">
              <div className="display text-2xl">{b.zone_name}</div>
              <div className="text-xs text-[#6B4E9E] uppercase tracking-widest mt-1">
                {b.persons.length} pass{b.persons.length > 1 ? "es" : ""} · {b.check_in} → {b.check_out}
              </div>
            </div>
            {open[b.id] ? <ChevronUp /> : <ChevronDown />}
          </button>

          {open[b.id] && (
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {b.persons.map((p) => (
                <div key={p.id} className="kf-border bg-[#FFF4E6] p-4 text-center" data-testid={`qr-card-${p.id}`}>
                  <div className="bg-white kf-border p-3 inline-block">
                    <QRCodeCanvas id={`qr-${p.id}`} value={p.id} size={160} bgColor="#ffffff" fgColor="#3D1E6D" level="H" />
                  </div>
                  <div className="mt-3 font-bold display text-xl">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] break-all">{p.id.slice(0, 8)}…</div>
                  <div className="mt-2 flex flex-wrap gap-1 justify-center">
                    {p.is_primary && <Badge color="magenta">Primary</Badge>}
                    <Badge color={p.status === "checked-in" ? "green" : p.status === "checked-out" ? "teal" : p.status === "overdue" ? "red" : "yellow"}>{p.status}</Badge>
                  </div>
                  <BrutalButton variant="outline" className="mt-3 px-3 py-1.5 text-[10px]" onClick={() => downloadQR(p.id, p.name)} data-testid={`qr-download-${p.id}`}>
                    <Download className="h-3 w-3" /> Download
                  </BrutalButton>
                </div>
              ))}
              {b.camping_type === "car" && b.car_details && (
                <div className="kf-border bg-[#FFB400] p-4 text-center" data-testid={`qr-car-${b.id}`}>
                  <div className="bg-white kf-border p-3 inline-block">
                    <QRCodeCanvas id={`qr-car-${b.id}`} value={`car:${b.id}`} size={160} bgColor="#ffffff" fgColor="#3D1E6D" level="H" />
                  </div>
                  <div className="mt-3 font-bold display text-xl flex items-center justify-center gap-2"><Car className="h-5 w-5" />{b.car_details.plate_number || "Car Pass"}</div>
                  <div className="text-xs text-[#3D1E6D] mt-1">{b.car_details.model} · {b.car_details.color}</div>
                  <Badge color="magenta" className="mt-2">Vehicle Pass</Badge>
                </div>
              )}
            </div>
          )}
        </BrutalCard>
      ))}
    </div>
  );
}
